import React, { useState } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import { toast } from 'sonner';
import { HostSettingsCard, HostPrimaryButton } from './HostSettingsCard';
import { GoogleIcon } from './GoogleIcon';
import { setMyPassword, disconnectGoogle, connectGoogle } from '../utils/accountSettingsApi';

const USE_CUSTOM_OAUTH = String(process.env.REACT_APP_USE_CUSTOM_GOOGLE_OAUTH || '').toLowerCase() === 'true';

const inputStyle = {
  width: '100%', boxSizing: 'border-box', padding: '10px 14px', borderRadius: 14,
  border: '1px solid var(--card-border)', background: 'var(--surface)', fontFamily: "'Inria Serif', serif",
  fontSize: 16, color: 'var(--text)', outline: 'none',
};

// Renders the "Sign-in method" card on Login & Security pages. Lets the user:
//  - set a password (if they signed up with Google only),
//  - disconnect Google (safe: requires a password is already set),
//  - reconnect Google (verifies Google email matches the account email).
// Pass `security` (from /api/account/settings) and `onChanged` so the parent
// page can refetch state after any mutation.
export function SignInMethodCard({ security, onChanged }) {
  const hasPassword = !!security?.has_password;
  const googleEnabled = !!security?.google_login_enabled;
  const email = security?.email || '';

  if (USE_CUSTOM_OAUTH) {
    return (
      <CardInner
        hasPassword={hasPassword}
        googleEnabled={googleEnabled}
        email={email}
        onChanged={onChanged}
      />
    );
  }
  // Custom OAuth is OFF: connect/disconnect actions require GIS, so we hide
  // the interactive controls and just surface the status (no provider yet).
  return null;
}

function CardInner({ hasPassword, googleEnabled, email, onChanged }) {
  const [mode, setMode] = useState('idle'); // 'idle' | 'set-password' | 'confirm-disconnect'
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const handleSetPassword = async () => {
    if (!password || password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setBusy(true);
    try {
      await setMyPassword({ password });
      toast.success('Password set. You can now sign in with email + password.');
      setPassword('');
      setConfirmPassword('');
      setMode('idle');
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to set password');
    } finally {
      setBusy(false);
    }
  };

  const handleConfirmDisconnect = async () => {
    setBusy(true);
    try {
      await disconnectGoogle();
      toast.success('Google sign-in disconnected. Use your email and password from now on.');
      setMode('idle');
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to disconnect Google');
    } finally {
      setBusy(false);
    }
  };

  const connect = useGoogleLogin({
    flow: 'implicit',
    scope: 'openid email profile',
    onSuccess: async (tokenResponse) => {
      try {
        await connectGoogle({ accessToken: tokenResponse.access_token });
        toast.success('Google connected. Faster sign-in is enabled.');
        onChanged?.();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to connect Google');
      } finally {
        setBusy(false);
      }
    },
    onError: () => {
      setBusy(false);
      toast.error('Google sign-in was cancelled.');
    },
  });

  return (
    <HostSettingsCard data-testid="signin-method-card">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <GoogleIcon size={22} />
        <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 18, color: 'var(--text)', margin: 0 }}>
          Sign-in method
        </p>
      </div>

      {/* Status row */}
      <div
        data-testid="signin-method-status"
        style={{ fontFamily: "'Inria Serif', serif", fontSize: 14, color: 'var(--text-2)', margin: '4px 0 14px', lineHeight: 1.5 }}
      >
        {googleEnabled ? (
          <>
            <strong style={{ color: 'var(--text)' }}>Google</strong> · {email}
            <br />
            {hasPassword
              ? 'You can sign in with Google or with email + password.'
              : 'You sign in with Google. Set a password to also enable email sign-in.'}
          </>
        ) : (
          <>
            <strong style={{ color: 'var(--text)' }}>Email + password</strong> · {email}
            <br />
            Connect Google for one-tap sign-in next time.
          </>
        )}
      </div>

      {/* Actions */}
      {mode === 'idle' && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {googleEnabled && !hasPassword && (
            <HostPrimaryButton
              onClick={() => setMode('set-password')}
              dataTestId="signin-method-set-password-button"
            >
              Set a password
            </HostPrimaryButton>
          )}
          {googleEnabled && hasPassword && (
            <button
              type="button"
              onClick={() => setMode('confirm-disconnect')}
              data-testid="signin-method-disconnect-button"
              style={{
                padding: '10px 18px', borderRadius: 999, border: '1px solid var(--card-border)',
                background: 'var(--surface)', fontFamily: "'Inria Serif', serif", fontSize: 15,
                color: 'var(--text)', cursor: 'pointer',
              }}
            >
              Disconnect Google
            </button>
          )}
          {!googleEnabled && (
            <button
              type="button"
              disabled={busy}
              onClick={() => { setBusy(true); connect(); }}
              data-testid="signin-method-connect-button"
              className="auth-secondary-btn"
              style={{ maxWidth: 260 }}
            >
              <GoogleIcon size={18} />
              {busy ? 'Opening Google…' : 'Connect Google'}
            </button>
          )}
        </div>
      )}

      {/* Set-password sub-form */}
      {mode === 'set-password' && (
        <div data-testid="signin-method-set-password-form" style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 4 }}>
          <input
            type="password"
            placeholder="New password (6+ chars)"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="signin-method-new-password-input"
            style={inputStyle}
          />
          <input
            type="password"
            placeholder="Confirm new password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            data-testid="signin-method-confirm-password-input"
            style={inputStyle}
          />
          <div style={{ display: 'flex', gap: 10 }}>
            <HostPrimaryButton
              onClick={handleSetPassword}
              disabled={busy}
              dataTestId="signin-method-save-password-button"
            >
              {busy ? 'Saving…' : 'Save password'}
            </HostPrimaryButton>
            <button
              type="button"
              onClick={() => { setMode('idle'); setPassword(''); setConfirmPassword(''); }}
              data-testid="signin-method-cancel-password-button"
              style={{
                padding: '10px 18px', borderRadius: 999, border: '1px solid var(--card-border)',
                background: 'var(--surface)', fontFamily: "'Inria Serif', serif", fontSize: 15,
                color: 'var(--text)', cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Disconnect confirmation */}
      {mode === 'confirm-disconnect' && (
        <div data-testid="signin-method-disconnect-confirm" style={{ marginTop: 4 }}>
          <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 14, color: 'var(--text-2)', lineHeight: 1.5, margin: '0 0 12px' }}>
            After disconnecting, you&apos;ll sign in with your email and password only. You can reconnect Google anytime.
          </p>
          <div style={{ display: 'flex', gap: 10 }}>
            <HostPrimaryButton
              onClick={handleConfirmDisconnect}
              disabled={busy}
              dataTestId="signin-method-confirm-disconnect-button"
            >
              {busy ? 'Disconnecting…' : 'Yes, disconnect'}
            </HostPrimaryButton>
            <button
              type="button"
              onClick={() => setMode('idle')}
              data-testid="signin-method-cancel-disconnect-button"
              style={{
                padding: '10px 18px', borderRadius: 999, border: '1px solid var(--card-border)',
                background: 'var(--surface)', fontFamily: "'Inria Serif', serif", fontSize: 15,
                color: 'var(--text)', cursor: 'pointer',
              }}
            >
              Keep Google connected
            </button>
          </div>
        </div>
      )}
    </HostSettingsCard>
  );
}
